import { Head, Link, router, useForm } from '@inertiajs/react'
import { FormEvent, useCallback, useEffect, useState } from 'react'
import { pushPage, goBack, goForward, canGoBack, canGoForward } from '@/shared/lib/pageHistory'
import MarkdownEditor from '@/widgets/editor/MarkdownEditor'
import { withWorkspaceLayout } from '@/app/layouts/WorkspaceLayout'
import Breadcrumbs, { type BreadcrumbItem } from '@/widgets/navigation/Breadcrumbs'
import { type PageNode } from '@/widgets/sidebar/PageTree'

interface User {
  id: number
  name: string
}

interface PageData {
  id: number
  title: string
  slug: string
  path: string
  content_md: string
  parent_id: number | null
  is_public: boolean
  is_folder: boolean
  created_by: User
  updated_by: User
  updated_at: string
}

interface Workspace {
  id: number
  name: string
  slug: string
}

interface ShowProps {
  workspace: Workspace
  page: PageData
  backlinks: { id: number; title: string; slug: string; path: string; title_path: string }[]
  sidebar_pages: PageNode[]
  breadcrumbs: BreadcrumbItem[]
  user_role: 'admin' | 'editor' | 'viewer'
}

function Show({ workspace, page, backlinks, breadcrumbs, user_role }: ShowProps) {
  const isViewer = user_role === 'viewer'
  const [suggesting, setSuggesting] = useState(false)
  const canEdit = !isViewer || suggesting

  const { data, setData, patch, processing, errors } = useForm({
    title: page.title,
    content_md: page.content_md,
    is_public: page.is_public,
  })

  const [saved, setSaved] = useState(false)
  const [hasBack, setHasBack] = useState(false)
  const [hasForward, setHasForward] = useState(false)

  useEffect(() => {
    const currentUrl = `/${workspace.slug}/${page.path}`
    pushPage(currentUrl)
    setHasBack(canGoBack())
    setHasForward(canGoForward())
  }, [page.path, workspace.slug])

  function handleBack() {
    const prev = goBack()
    if (prev) router.visit(prev)
  }

  function handleForward() {
    const next = goForward()
    if (next) router.visit(next)
  }

  const save = useCallback(() => {
    if (!canEdit || processing) return
    patch(`/${workspace.slug}/${page.path}`, {
      onSuccess: () => {
        setSaved(true)
        if (suggesting) setSuggesting(false)
        setTimeout(() => setSaved(false), 2000)
      },
    })
  }, [canEdit, processing, patch, workspace.slug, page.path, suggesting])

  function handleSubmit(e: FormEvent) {
    e.preventDefault()
    save()
  }

  useEffect(() => {
    function handleKeyDown(e: KeyboardEvent) {
      if ((e.metaKey || e.ctrlKey) && e.key === 's') {
        e.preventDefault()
        save()
      }
    }
    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [save])

  return (
    <>
      <Head title={`${page.title} - ${workspace.name}`} />
      <div className="mx-auto max-w-4xl px-6 py-8">
        <form onSubmit={handleSubmit}>
          <div className="mb-6 flex items-center gap-3">
            <div className="flex shrink-0 items-center rounded-lg border border-gray-200 bg-gray-50">
              <button
                type="button"
                onClick={handleBack}
                disabled={!hasBack}
                className={`rounded-l-lg p-2 ${hasBack ? 'text-gray-700 hover:bg-gray-200' : 'cursor-default text-gray-300'}`}
                title="Back"
              >
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="h-5 w-5">
                  <path fillRule="evenodd" d="M17 10a.75.75 0 0 1-.75.75H5.612l4.158 3.96a.75.75 0 1 1-1.04 1.08l-5.5-5.25a.75.75 0 0 1 0-1.08l5.5-5.25a.75.75 0 1 1 1.04 1.08L5.612 9.25H16.25A.75.75 0 0 1 17 10Z" clipRule="evenodd" />
                </svg>
              </button>
              <div className="h-5 w-px bg-gray-200" />
              <button
                type="button"
                onClick={handleForward}
                disabled={!hasForward}
                className={`rounded-r-lg p-2 ${hasForward ? 'text-gray-700 hover:bg-gray-200' : 'cursor-default text-gray-300'}`}
                title="Forward"
              >
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="h-5 w-5">
                  <path fillRule="evenodd" d="M3 10a.75.75 0 0 1 .75-.75h10.638l-4.158-3.96a.75.75 0 1 1 1.04-1.08l5.5 5.25a.75.75 0 0 1 0 1.08l-5.5 5.25a.75.75 0 1 1-1.04-1.08l4.158-3.96H3.75A.75.75 0 0 1 3 10Z" clipRule="evenodd" />
                </svg>
              </button>
            </div>
            <Breadcrumbs
              workspaceName={workspace.name}
              workspaceSlug={workspace.slug}
              crumbs={breadcrumbs}
            />
          </div>

          {isViewer && !suggesting && !page.is_folder && (
            <div className="mb-4 flex items-center justify-between rounded-md bg-gray-50 border border-gray-200 px-4 py-2">
              <span className="text-sm text-gray-600">You have view-only access.</span>
              <button
                type="button"
                onClick={() => setSuggesting(true)}
                className="text-sm font-medium text-indigo-600 hover:text-indigo-800"
              >
                Suggest edits
              </button>
            </div>
          )}
          {suggesting && (
            <div className="mb-4 rounded-md bg-amber-50 border border-amber-200 px-4 py-2 text-sm text-amber-700">
              Suggesting mode — your changes will be submitted for review.
            </div>
          )}

          <div className="flex items-center gap-4">
            <input
              type="text"
              value={data.title}
              onChange={(e) => setData('title', e.target.value)}
              readOnly={isViewer}
              className={`min-w-0 flex-1 border-0 bg-transparent text-3xl font-bold text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-0 ${isViewer ? 'cursor-default' : ''}`}
              placeholder="Page title"
            />
            {canEdit && (
              <div className="flex shrink-0 items-center gap-4">
                {!isViewer && (
                  <label className="flex items-center gap-2 text-sm text-gray-500">
                    <input
                      type="checkbox"
                      checked={data.is_public}
                      onChange={(e) => setData('is_public', e.target.checked)}
                      className="h-3.5 w-3.5 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                    />
                    Public
                  </label>
                )}
                {saved && (
                  <span className="text-sm text-green-600">{suggesting ? 'Submitted!' : 'Saved'}</span>
                )}
                {suggesting && (
                  <button
                    type="button"
                    onClick={() => { setSuggesting(false); setData({ title: page.title, content_md: page.content_md, is_public: page.is_public }) }}
                    className="text-sm text-gray-500 hover:text-gray-700"
                  >
                    Cancel
                  </button>
                )}
                <button
                  type="submit"
                  disabled={processing}
                  className={`rounded-md px-4 py-1.5 text-sm font-medium text-white disabled:opacity-50 ${suggesting ? 'bg-amber-600 hover:bg-amber-700' : 'bg-indigo-600 hover:bg-indigo-700'}`}
                >
                  {processing ? (suggesting ? 'Submitting...' : 'Saving...') : suggesting ? 'Submit suggestion' : 'Save'}
                </button>
              </div>
            )}
          </div>
          {errors.title && (
            <p className="mt-1 text-sm text-red-600">{errors.title}</p>
          )}

          {!page.is_folder && (
            <div className="mt-4">
              <MarkdownEditor
                content={data.content_md}
                onChange={(markdown) => setData('content_md', markdown)}
                editable={canEdit}
                placeholder="Write your content in markdown..."
                workspaceSlug={workspace.slug}
                pageId={page.id}
                borderless
              />
              {errors.content_md && (
                <p className="mt-1 text-sm text-red-600">{errors.content_md}</p>
              )}
            </div>
          )}

          <div className="mt-6 text-sm text-gray-400">
            Last updated by {page.updated_by.name} on{' '}
            {new Date(page.updated_at).toLocaleDateString()}
          </div>
        </form>

        {backlinks.length > 0 && (
          <div className="mt-12 border-t border-gray-200 pt-8">
            <h3 className="text-lg font-medium text-gray-900">Backlinks</h3>
            <ul className="mt-4 space-y-2">
              {backlinks.map((link) => (
                <li key={link.id}>
                  <Link
                    href={`/${workspace.slug}/${link.path}`}
                    className="text-sm text-indigo-600 hover:text-indigo-800 hover:underline"
                  >
                    {link.title_path}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </>
  )
}

Show.layout = withWorkspaceLayout
export default Show
